// Dialog bei echten Feldkonflikten auf der Self-Service-Flaeche (Spec 2026-09-15 §4).
// Bis 2026-09-14 war das der Dreiwege-Dialog „ganz oder gar nicht"; seit dem
// feldgenauen Abgleich erscheint er nur noch, wenn BEIDE Seiten dasselbe Feld
// unterschiedlich geaendert haben — alles andere hat die Seite schon
// zusammengefuehrt. „Neu laden" entfaellt: der Abgleich hat bereits geladen.
//
// Eigenes Modul statt `VoiceConflictModal` aus dem Admin-Bereich: die
// Self-Service-Flaeche liegt ausserhalb von Auth, hat ihr eigenes Bundle und
// darf kein Admin-Modul importieren. Der sichtbare Unterschied ist die
// Identitaet — hier steht immer nur „von TRO", nie der Name einer Kollegin, und
// seit Spec §6 keine Uhrzeit (die einzige ehrliche Zeit waere das interne
// `updated_at`, das die Self-Flaeche nicht mehr bekommt).
import ConfirmModal from '@/components/ConfirmModal';
import { selfLabelsFor, type FormState } from './selfFieldLabels';
import { MESSAGES, useT, type Lang } from './i18n/LanguageProvider';

export interface SelfConflict {
  /** Felder, die beide Seiten seit dem Laden unterschiedlich geaendert haben. */
  fields: (keyof FormState)[];
  /** Frischer Serverstand als Formular — Grundlage fuer „Fremde übernehmen". */
  theirs: FormState | null;
  /**
   * Warum es KEINE Feldliste gibt — nur gesetzt, wenn `fields` leer ist:
   * `'repeated'` = dreimal in Folge fremdgespeichert, `'reload_failed'` = der
   * frische Stand war nicht ladbar. Der Dialog sagt in beiden Faellen etwas
   * anderes; ohne die Unterscheidung stuende dort eine Aussage, die nicht stimmt.
   */
  reason?: 'repeated' | 'reload_failed';
  /**
   * Woher der Konflikt kommt — und damit, was die Knoepfe tun: `'save'`
   * (Standard) = beim Speichern aufgetreten, jede Antwort speichert erneut.
   * `'adopt'` = beim „Übernehmen" des Aktualitaets-Banners (Spec §5): dort wird
   * NUR das Formular gesetzt, geschrieben wird nichts.
   */
  mode?: 'save' | 'adopt';
}

export function selfConflictMessage(c: SelfConflict, lang: Lang = 'de'): string {
  const t = MESSAGES[lang];
  const felder = c.fields.length > 0
    ? t.conflictFields(selfLabelsFor(c.fields, lang))
    : c.reason === 'reload_failed'
      ? t.conflictReloadFailed
      : t.conflictRepeated;
  return `${t.conflictIntro} ${felder}`;
}

/**
 * Antworten des Dialogs. Seit Spec 2026-09-15 §7 (Philip: „so simpel wie möglich
 * für Sprecher") ohne „Abbrechen": im Übernehmen-Pfad wirkte es stillschweigend
 * wie „Meine behalten". `'dismiss'` ist das „OK" des Dialogs ohne Feldliste —
 * schliesst und laesst das Formular, wie es ist.
 */
export type SelfConflictChoice = 'keepMine' | 'takeTheirs' | 'dismiss';

export default function SelfConflictModal(
  { conflict, busy, onResolve }: { conflict: SelfConflict | null; busy: boolean; onResolve: (c: SelfConflictChoice) => void },
) {
  // „Fremde übernehmen" setzt genau die strittigen Felder auf den fremden Wert.
  // Ohne Feldliste (Stand nicht ladbar, mehrfach fremdgespeichert) gibt es nichts
  // abzuwaegen: dann nur „OK", das Formular bleibt und „Speichern" versucht es
  // erneut. Kein Abbrechen, kein Schliessen per Hintergrund — der Dialog verlangt
  // eine der angebotenen Antworten.
  const feldgenau = (conflict?.fields.length ?? 0) > 0;
  const { lang, t } = useT();
  return (
    <ConfirmModal
      open={conflict !== null}
      title={t.conflictTitle}
      message={conflict ? selfConflictMessage(conflict, lang) : ''}
      confirmLabel={feldgenau ? t.conflictKeepMine : t.conflictOk}
      busy={busy}
      busyLabel={t.saving}
      secondaryLabel={feldgenau ? t.conflictTakeTheirs : undefined}
      hideCancel
      dismissible={false}
      onConfirm={() => onResolve(feldgenau ? 'keepMine' : 'dismiss')}
      onSecondary={feldgenau ? () => onResolve('takeTheirs') : undefined}
    />
  );
}
